import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../utils/authFetch";

const initialForm = {
  name: "",
  partita_iva: "",
  codice_fiscale: "",
  email: "",
  telefono: "",
  indirizzo: "",
  cap: "",
  citta: "",
  provincia: "",
  unique_code: "",
  pec: "",
};

const requiredFields = [
  ["name", "Nome azienda"],
  ["partita_iva", "Partita IVA"],
  ["codice_fiscale", "Codice fiscale"],
  ["email", "Email"],
  ["telefono", "Telefono"],
  ["indirizzo", "Indirizzo"],
  ["cap", "CAP"],
  ["citta", "Città"],
  ["provincia", "Provincia"],
];

export default function AppAgenteAcquisisciAziendaPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (error) setError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const missing = requiredFields
      .filter(([field]) => !form[field].trim())
      .map(([, label]) => label);

    if (missing.length) {
      setError("Compila i campi obbligatori: " + missing.join(", "));
      return;
    }

    const uniqueCode = form.unique_code.trim().toUpperCase();
    const pec = form.pec.trim();

    if (!uniqueCode && !pec) {
      setError("Inserisci almeno uno tra Codice SDI e PEC.");
      return;
    }

    if (uniqueCode && !/^[A-Z0-9]{7}$/.test(uniqueCode)) {
      setError("Il Codice SDI deve contenere 7 caratteri alfanumerici.");
      return;
    }

    if (pec && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(pec)) {
      setError("Inserisci un indirizzo PEC valido.");
      return;
    }

    try {
      setSaving(true);
      setError("");

      const response = await authFetch("/api/agente/aziende/acquisisci/", {
        method: "POST",
        json: {
          name: form.name.trim(),
          vat: form.partita_iva.trim(),
          cf: form.codice_fiscale.trim(),
          email: form.email.trim(),
          phone: form.telefono.trim(),
          address: form.indirizzo.trim(),
          cap: form.cap.trim(),
          citta: form.citta.trim(),
          provincia: form.provincia.trim().toUpperCase(),
          unique_code: uniqueCode,
          pec,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data?.detail ||
          data?.error ||
          "Impossibile acquisire la nuova azienda."
        );
      }

      navigate("/app", {
        replace: true,
        state: {
          agenteMessage: `${data?.azienda?.name || form.name} acquisita correttamente.`,
        },
      });
    } catch (err) {
      setError(err?.message || "Errore durante il salvataggio.");
    } finally {
      setSaving(false);
    }
  };

  const inputClass = "form-control rounded-3";

  const field = (name, label, type = "text", options = {}) => (
    <>
      <label htmlFor={`azienda-${name}`} className="form-label fw-semibold">
        {label}{options.required !== false ? " *" : ""}
      </label>
      <input
        id={`azienda-${name}`}
        type={type}
        className={inputClass}
        name={name}
        value={form[name]}
        onChange={handleChange}
        required={options.required !== false}
        maxLength={options.maxLength}
        autoComplete={options.autoComplete}
        disabled={saving}
      />
    </>
  );

  return (
    <div className="container py-4" style={{ maxWidth: 760 }}>
      <button
        type="button"
        className="btn btn-link text-decoration-none px-0 mb-3"
        onClick={() => navigate("/app")}
      >
        ← Torna alla home
      </button>

      <div className="mb-4">
        <div className="small text-uppercase text-muted fw-bold">
          Area Agente
        </div>
        <h2 className="fw-bold mb-1">Acquisisci nuova azienda</h2>
        <div className="text-muted">
          La nuova azienda verrà collegata automaticamente al tuo portafoglio.
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="card border-0 shadow-sm" style={{ borderRadius: 18 }}>
          <div className="card-body p-4">
            {error && (
              <div className="alert alert-danger rounded-4" role="alert">
                {error}
              </div>
            )}

            <div className="mb-3">
              {field("name", "Nome azienda")}
            </div>

            <div className="row g-3 mb-3">
              <div className="col-12 col-md-6">
                {field("partita_iva", "Partita IVA", "text", { maxLength: 20 })}
              </div>
              <div className="col-12 col-md-6">
                {field("codice_fiscale", "Codice fiscale", "text", { maxLength: 20 })}
              </div>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-12 col-md-6">
                {field("email", "Email", "email")}
              </div>
              <div className="col-12 col-md-6">
                {field("telefono", "Telefono", "tel", { maxLength: 20 })}
              </div>
            </div>

            <div className="mb-3">
              {field("indirizzo", "Indirizzo")}
            </div>

            <div className="row g-3 mb-3">
              <div className="col-12 col-md-4">
                {field("cap", "CAP", "text", { maxLength: 10 })}
              </div>
              <div className="col-12 col-md-5">
                {field("citta", "Città")}
              </div>
              <div className="col-12 col-md-3">
                {field("provincia", "Provincia", "text", { maxLength: 2 })}
              </div>
            </div>

            <hr className="my-4" />

            <h5 className="fw-bold mb-2">Dati per la fatturazione elettronica</h5>
            <p className="text-muted small mb-3">
              Compila almeno uno dei due campi: Codice SDI oppure PEC.
              Puoi inserirli anche entrambi.
            </p>

            <div className="row g-3 mb-4">
              <div className="col-12 col-md-5">
                {field("unique_code", "Codice SDI", "text", {
                  required: false,
                  maxLength: 7,
                })}
              </div>
              <div className="col-12 col-md-7">
                {field("pec", "PEC", "email", { required: false })}
              </div>
            </div>

            <div className="small text-muted mb-3">
              * Campi obbligatori
            </div>

            <button
              type="submit"
              className="btn btn-dark w-100 rounded-3 py-3 fw-bold"
              disabled={saving}
            >
              {saving ? "Salvataggio..." : "Acquisisci azienda"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
